/**
 * reservationProgress.ts — 貸出返却リストの「対応中」判定（2026-09-14）
 *
 * 行の背景を黄色く点滅させて「今この予約に誰かが動いている」を示す。
 * 判定は **操作ではなく状態** から導く。車両ポジションの DnD で動かしても、
 * リストのステータス変更で動かしても、他の端末で動かしても同じに見える。
 * 新しい保存項目は増やさず、車両ステータスが変われば自動的に止まる。
 *
 *   貸出行: 車がピックアップ系（迎え中 / 赤嶺駅P.U / 下地迎）に居る、
 *           または「連絡」が入っていて直接来店以外（連絡あり / ロビー / 14番 / 送迎車 / 返却車。
 *           2026-09-19 追加。お客様が着いていて誰かが動いている状態）
 *           → 出発中になったら止まる（予約も利用中になり完了表示に変わる）
 *   返却行: 返却予約がまだ利用中で、車が出発中では **なくなった**
 *           （店舗に戻った / ドロップオフ中 / 店舗→ドロップオフ→店舗 の往復中）
 *           → 貸出済みになったら止まる
 *
 * 返却行を「ドロップオフ中だけ」にしなかったのは前田さんの指示（2026-09-14）。
 * 店舗に戻ったあと「貸出済みにしますか？」で いいえ を選んだ状態（カウンターで
 * 返却手続き中）も対応中として見せる。どこに居るかは行の車両ステータスチップで分かる。
 *
 * 副作用として「車を出発中にし忘れたまま返却日を迎えた」予約も点滅する
 * （車が店舗に居て予約が利用中＝矛盾した状態）。これは不整合の可視化として歓迎する。
 */
import { getVehicleZonesConfig } from '../config/vehicleZones';
import { ArrivalContact, isArrivalContactInProgress } from './arrivalContact';

const cfg = getVehicleZonesConfig();

const statusCodesOf = (zoneIds: Set<string>): Set<string> => new Set(
  cfg.zones
    .filter(z => z.droppable !== false && zoneIds.has(z.id) && z.statusCode)
    .map(z => z.statusCode as string),
);

/** ピックアップ系の車両ステータス code（店舗別。那覇: p_u / akamine_south_pu） */
export const PICKUP_STATUS_CODES: Set<string> = statusCodesOf(cfg.pickupZoneIds);
/** ドロップオフ系の車両ステータス code（店舗別。那覇: d_o / akamine_south_do） */
export const DROPOFF_STATUS_CODES: Set<string> = statusCodesOf(cfg.dropoffZoneIds);

const DEPARTING_CODE = 'departing';
const IN_USE_CODES = new Set(['in_use', '3']);

export interface ProgressInput {
  rowType: 'departure' | 'return';
  /** 行の予約に紐づく車両の車両ステータス code。車両未アサインなら null */
  vehicleStatusCode: string | null | undefined;
  /** 予約ステータス（code または id 文字列） */
  reservationStatus: string | number | null | undefined;
  /** 貸出行の「連絡」（到着連絡 / 空港）。省略時は連絡なし扱い */
  arrivalContact?: ArrivalContact | null;
  /**
   * 予約種別が米軍か。米軍は長期貸出で、利用中の間は車両ステータスが「米軍」のまま（出発中にならない）ため、
   * 返却行の判定（利用中なのに出発中でない）に常に当たってしまう。利用中なら光らせない（2026-09-25）
   */
  isUsMilitary?: boolean;
}

/** この行が「対応中」（黄色点滅）か */
export const isReservationInProgress = ({ rowType, vehicleStatusCode, reservationStatus, arrivalContact, isUsMilitary }: ProgressInput): boolean => {
  const resStatus = String(reservationStatus ?? '').toLowerCase();

  // 米軍予約は利用中になったら光らせない（前田さん指示 2026-09-25）
  if (isUsMilitary && IN_USE_CODES.has(resStatus)) return false;

  if (rowType === 'departure') {
    // 出発中になった時点で予約は利用中になり、ここは false になる
    if (IN_USE_CODES.has(resStatus)) return false;
    // 迎えに向かっている間（車両が無ければ判定できない）
    if (vehicleStatusCode && PICKUP_STATUS_CODES.has(vehicleStatusCode)) return true;
    // 「連絡」が入っている（直接来店を除く）。車両未アサインでも連絡が入れば対応中
    return !!arrivalContact && isArrivalContactInProgress(arrivalContact);
  }

  if (!vehicleStatusCode) return false;

  // 返却: 予約がまだ利用中 なのに 車が出発中ではない ＝ 戻ってきて手続き中
  return IN_USE_CODES.has(resStatus) && vehicleStatusCode !== DEPARTING_CODE;
};
